import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import excelLogo from '../../../assets/Microsoft_Office_Excel_logo.png';
import sampleSpreadsheet from '../../../assets/Sample_excel_spreadsheet.jpg';

interface UploadZoneProps {
  onFile: (file: File) => void;
  isLoading: boolean;
  filename?: string;
}

export default function UploadZone({ onFile, isLoading, filename }: UploadZoneProps) {
  const [dragActive, setDragActive] = useState(false);
  const [showSample, setShowSample] = useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted) => {
      if (accepted.length > 0) onFile(accepted[0]);
    },
    onDragEnter: () => setDragActive(true),
    onDragLeave: () => setDragActive(false),
    onDropAccepted: () => setDragActive(false),
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
    disabled: isLoading,
  });

  const active = isDragActive || dragActive;

  return (
    <>
      <div
        {...getRootProps()}
        className={[
          'relative flex h-full min-h-44 cursor-pointer flex-col items-center justify-center rounded-lg border px-8 pb-12 pt-8 text-center shadow-sm transition-all',
          active
            ? 'border-slate-400 bg-slate-50 ring-4 ring-slate-100'
            : isLoading
            ? 'border-slate-300 bg-slate-50 cursor-wait'
            : 'border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50',
        ].join(' ')}
      >
        <input {...getInputProps()} />

        {isLoading ? (
          <LoadingState filename={filename} />
        ) : filename ? (
          <FileReadyState filename={filename} />
        ) : (
          <IdleState active={active} />
        )}

        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setShowSample(true);
          }}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-medium text-slate-500 underline underline-offset-2 transition-colors hover:text-slate-900"
        >
          Click here to see sample Xero Account Transaction spreadsheet
        </button>
      </div>

      {showSample && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-6"
          onClick={() => setShowSample(false)}
        >
          <div
            className="relative w-full max-w-5xl rounded-lg border border-slate-300 bg-white p-4 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowSample(false)}
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 bg-white text-lg leading-none text-slate-600 shadow-sm hover:bg-slate-50 hover:text-slate-950"
              aria-label="Close sample spreadsheet preview"
            >
              ×
            </button>
            <img
              src={sampleSpreadsheet}
              alt="Sample Xero Account Transaction spreadsheet"
              className="mt-8 w-full rounded-md border border-slate-200"
            />
          </div>
        </div>
      )}
    </>
  );
}

function IdleState({ active }: { active: boolean }) {
  return (
    <>
      <div className={[
        'mb-4 flex h-12 w-12 items-center justify-center rounded-md transition-colors',
        active ? 'bg-white' : 'bg-white',
      ].join(' ')}>
        <img src={excelLogo} alt="Excel spreadsheet" className="h-9 w-9 object-contain" />
      </div>
      <p className={['text-sm font-semibold transition-colors', active ? 'text-slate-900' : 'text-slate-900'].join(' ')}>
        Drag &amp; Drop your Xero Excel report file here
        <br />
        or click to browse.
      </p>
    </>
  );
}

function LoadingState({ filename }: { filename?: string }) {
  return (
    <>
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-slate-100">
        <svg className="h-6 w-6 animate-spin text-slate-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
      <p className="text-sm font-semibold text-slate-900">Parsing report...</p>
      {filename && <p className="mt-1 text-xs text-slate-500 truncate max-w-xs">{filename}</p>}
    </>
  );
}

function FileReadyState({ filename }: { filename: string }) {
  return (
    <>
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-white">
        <img src={excelLogo} alt="Excel spreadsheet" className="h-9 w-9 object-contain" />
      </div>
      <p className="text-sm font-semibold text-slate-900 truncate max-w-xs">{filename}</p>
      <p className="mt-1 text-xs text-slate-500">Click or drop to replace</p>
    </>
  );
}
